// Auth store — session state for the console.
//
// Task 326.18 (decision Q7; findings WEB-001, WEB-002, WEB-003, WEB-004, WEB-016):
//
//   * The refresh token is GONE from the client. It is an httpOnly cookie set by the BFF; this store
//     keeps only the 15-minute access token, in memory. `bancorix:rt` is no longer written, and any
//     value left over from an older build is purged on load.
//   * `availableTenants` is the field the server actually sends (it was read as `tenants`, so it was
//     always empty and multi-tenant operators could not switch tenant — decision Q22). It arrives on
//     login AND on refresh, so a browser reload keeps the switcher populated.
//   * A session restore is a call to the BFF with the cookie, not a token read out of storage, so
//     `isInitializing` keys off a non-secret marker instead of a credential.
//   * logout() and switchTenant() purge document drafts: they hold customer PII keyed by document
//     type only, so the next operator on a shared workstation was offered the previous one's data
//     (WEB-004).
//
// Task 326.17 (decision Q27) still holds: the JWT carries roles only; permission codes come from
// GET api/console/me and are cached per `${userId}:${tenantId}`. Roles are not permissions.

import { create } from 'zustand';
import {
  switchTenant as switchTenantApi,
  logout as logoutApi,
  type SwitchTenantResponse,
} from '@/api/endpoints/auth';
import type { ConsoleSession, MfaStep, SessionUser, TenantRef } from '@/api/contracts/session';
import { decodeJwtPayload } from '@/shared/utils/jwt';
import { purgeAllDrafts } from '@/shared/utils/draftStorage';

export type { TenantRef, SessionUser };

/** Kept for the components that were written against this name. */
export type UserInfo = SessionUser;

/**
 * Marker that a session probably exists, so a cold start knows whether to block routing on a silent
 * restore. NOT a credential — the credential is the httpOnly cookie, which JavaScript cannot read.
 */
export const SESSION_HINT_KEY = 'bancorix:session';
export const LAST_TENANT_KEY = 'bancorix:lastTenantId';

/** Keys written by builds before 326.18. Removed on load: one held a live refresh token. */
const LEGACY_KEYS = ['bancorix:rt', 'bancorix:user', 'bancorix:tenants'];

function purgeLegacyKeys(): void {
  try {
    for (const key of LEGACY_KEYS) localStorage.removeItem(key);
  } catch {
    // storage unavailable (private browsing) — nothing to purge
  }
}

function readSessionHint(): boolean {
  try {
    return localStorage.getItem(SESSION_HINT_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSessionHint(present: boolean): void {
  try {
    if (present) localStorage.setItem(SESSION_HINT_KEY, '1');
    else localStorage.removeItem(SESSION_HINT_KEY);
  } catch {
    // ignore
  }
}

interface AuthState {
  token: string | null;
  user: UserInfo | null;
  /** Permission codes from GET api/console/me. Empty until the first /me resolves. */
  permissions: string[];
  /** Role codes from the access token. NOT permission codes — check them with hasRole(). */
  roles: string[];
  /**
   * `${userId}:${tenantId}` the cached permissions belong to, or null when none are loaded.
   * Lets the /me sync refetch exactly once per session and once per tenant switch.
   */
  permissionsFor: string | null;
  isAuthenticated: boolean;
  /** True while AuthInitializer is attempting a silent restore on startup. */
  isInitializing: boolean;
  /** Tenant ID parsed from the current access token. */
  currentTenantId: string | null;
  /** Every tenant the operator may act for. From login and from refresh. */
  availableTenants: TenantRef[];
  /** Server says the operator must change their password (task 276.11b). */
  requiresPasswordChange: boolean;

  /** Applies a session from login, refresh or invitation acceptance — one shape, one path. */
  applySession: (session: ConsoleSession, options?: { keepPermissions?: boolean }) => void;
  /** Ends the session locally: clears state, the session marker and any saved drafts. */
  logout: () => void;
  /** Best-effort server-side revocation, then the local clear. */
  signOut: () => Promise<void>;
  /** Applies a GET /me result. `scope` is `${userId}:${tenantId}` — see permissionsFor. */
  setPermissions: (permissions: string[], scope?: string | null) => void;
  /** True when the server granted this permission code to the caller. Never matches a role code. */
  hasPermission: (code: string) => boolean;
  /** True when the access token carries this role code (SYSTEM_ADMIN, TENANT_ADMIN, …). */
  hasRole: (code: string) => boolean;
  /**
   * Switches the active tenant. Replaces the access token and persists the selection.
   * Does NOT navigate and does NOT clear the React Query cache — both are the caller's job.
   *
   * Task 387.11.5: when the target tenant needs verification again, nothing changes and the step-up challenge is
   * returned instead; answer it (mfa/step-up/verify) and pass that response to completeTenantSwitch.
   */
  switchTenant: (tenantId: string) => Promise<MfaStep | null>;
  /** Applies a completed switch — the switch-tenant response, or the step-up verification that finished it. */
  completeTenantSwitch: (data: SwitchTenantResponse) => void;
}

// The MS-schema role claim ("SYSTEM_ADMIN", "TENANT_ADMIN", …). A string for a single
// role, an array for multiple.
const ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

// Role codes from the token. Permission codes are NOT in the token (task 326.17, decision Q27).
function parseRolesFromJwt(token: string): string[] {
  const decoded = decodeJwtPayload(token);
  if (!decoded) return [];

  const roleClaim = decoded[ROLE_CLAIM] ?? decoded['role'] ?? [];
  if (Array.isArray(roleClaim)) return roleClaim as string[];
  return typeof roleClaim === 'string' ? [roleClaim] : [];
}

function parseTenantIdFromJwt(token: string): string | null {
  const tenantId = decodeJwtPayload(token)?.['tenantId'];
  return typeof tenantId === 'string' ? tenantId : null;
}

purgeLegacyKeys();

export const useAuthStore = create<AuthState>((set, get) => ({
  token: null,
  user: null,
  permissions: [],
  roles: [],
  permissionsFor: null,
  isAuthenticated: false,
  // Only block routing when a session was probably left behind; otherwise routing is immediate.
  isInitializing: readSessionHint(),
  currentTenantId: null,
  availableTenants: [],
  requiresPasswordChange: false,

  applySession: (session, options) => {
    const roles = parseRolesFromJwt(session.accessToken);
    const currentTenantId = parseTenantIdFromJwt(session.accessToken) ?? session.user.tenantId;
    const keepPermissions = options?.keepPermissions === true;

    writeSessionHint(true);
    set({
      token: session.accessToken,
      user: session.user,
      roles,
      // Cleared unless this is a silent refresh of the same user and tenant: the /me sync refills
      // them, and stale codes must never outlive the grant that produced them.
      ...(keepPermissions ? {} : { permissions: [], permissionsFor: null }),
      isAuthenticated: true,
      isInitializing: false,
      currentTenantId,
      availableTenants: session.availableTenants,
      requiresPasswordChange: session.requiresPasswordChange,
    });
  },

  logout: () => {
    writeSessionHint(false);
    purgeLegacyKeys();
    // Drafts hold customer PII and are keyed by document type, not by operator or tenant.
    purgeAllDrafts();
    try {
      localStorage.removeItem(LAST_TENANT_KEY);
    } catch {
      // ignore
    }
    set({
      token: null,
      user: null,
      permissions: [],
      roles: [],
      permissionsFor: null,
      isAuthenticated: false,
      isInitializing: false,
      currentTenantId: null,
      availableTenants: [],
      requiresPasswordChange: false,
    });
  },

  signOut: async () => {
    // The cookie can only be cleared by the server that set it; a network failure must still leave
    // the client signed out.
    await logoutApi().catch(() => undefined);
    get().logout();
  },

  setPermissions: (permissions, scope = null) => {
    set({ permissions, permissionsFor: scope });
  },

  hasPermission: (code) => {
    return get().permissions.includes(code);
  },

  hasRole: (code) => {
    return get().roles.includes(code);
  },

  switchTenant: async (tenantId: string) => {
    const data = await switchTenantApi({ tenantId }, get().token);
    // A step-up carries no token: the current tenant stays active until the challenge is answered.
    if (data.mfa) return data.mfa;
    get().completeTenantSwitch(data);
    return null;
  },

  completeTenantSwitch: (data) => {
    const roles = parseRolesFromJwt(data.accessToken);
    const currentTenantId = parseTenantIdFromJwt(data.accessToken) ?? data.tenantId;

    try {
      localStorage.setItem(LAST_TENANT_KEY, currentTenantId);
    } catch {
      // ignore
    }

    // Drafts belong to the tenant they were typed in; carrying them across is a cross-tenant leak
    // inside the browser (WEB-004).
    purgeAllDrafts();

    // Grants are per tenant — drop the cache and let the /me sync refill it for the new tenant.
    set({
      token: data.accessToken,
      roles,
      permissions: [],
      permissionsFor: null,
      currentTenantId,
    });
  },
}));

// Convenience hook — matches naming convention used in components.
export const useAuth = () => useAuthStore();
